import { DEFAULT_ROOM_ID, NAME_MAX_LENGTH, type RoomPhase } from '@cube-rush/shared';
import type { TargetedSubmitEvent } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { useApp } from '../../app/context';
import { ru } from '../../i18n/ru';
import { Button } from '../components/Button';
import { Dialog } from '../components/Dialog';
import './RoomsDialog.css';

const PHASE_EMOJI: Readonly<Record<RoomPhase, string>> = {
  lobby: '🟢',
  countdown: '⏳',
  race: '🏁',
  results: '🏆',
};

export function RoomsDialog({ onClose }: { onClose(): void }) {
  const app = useApp();
  const rooms = app.room.rooms.value;
  const current = app.room.room.value;
  const online = app.connection.status.value === 'online';
  const [name, setName] = useState('');

  // The server pushes changes to the list on its own; this only makes sure it is fresh.
  useEffect(() => app.requestRooms(), [app]);

  const join = (roomId: string) => {
    app.joinRoom(roomId);
    onClose();
  };

  const create = (event: TargetedSubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (app.createRoom(name)) onClose();
  };

  return (
    <Dialog title={ru.rooms.title} onClose={onClose}>
      <ul class="rooms-list">
        {rooms.length === 0 && <li class="rooms-list__empty">{ru.rooms.empty}</li>}
        {rooms.map((room) => {
          const here = room.id === current?.id;
          const full = room.members >= room.maxMembers;
          return (
            <li key={room.id} class={`rooms-list__item${here ? ' rooms-list__item--here' : ''}`}>
              <span class="rooms-list__icon" aria-hidden="true">
                {PHASE_EMOJI[room.phase]}
              </span>
              <span class="rooms-list__main">
                <span class="rooms-list__name">{room.name}</span>
                <span class="rooms-list__meta">
                  {ru.room.phase[room.phase]} · 👥 {ru.rooms.members(room.members, room.maxMembers)}
                  {here && <span class="rooms-list__here">{ru.rooms.current}</span>}
                </span>
              </span>
              {!here && (
                <Button
                  size="small"
                  variant="green"
                  disabled={!online || full}
                  onClick={() => join(room.id)}
                >
                  {full ? ru.rooms.full : ru.rooms.join}
                </Button>
              )}
            </li>
          );
        })}
      </ul>

      <form class="rooms-create" onSubmit={create}>
        <h3 class="rooms-create__title">{ru.rooms.createTitle}</h3>
        <div class="rooms-create__row">
          <input
            class="field__input"
            value={name}
            maxLength={NAME_MAX_LENGTH}
            placeholder={ru.rooms.createPlaceholder}
            aria-label={ru.rooms.createLabel}
            enterKeyHint="done"
            onInput={(e) => setName(e.currentTarget.value)}
          />
          <Button type="submit" variant="yellow" disabled={!online || name.trim() === ''}>
            {ru.rooms.create}
          </Button>
        </div>
      </form>

      {current && current.id !== DEFAULT_ROOM_ID && (
        <Button
          variant="ghost"
          block
          icon="↩️"
          disabled={!online}
          onClick={() => {
            app.leaveRoom();
            onClose();
          }}
        >
          {ru.rooms.leave}
        </Button>
      )}
    </Dialog>
  );
}
